// @greenlight-spec: 28-it-dashboard-ui.md
// @greenlight-spec: 11-observability.md
// @greenlight-spec: 19-design-system.md
import * as React from 'react';

import { MonoValue } from '@/components/greenlight/MonoValue';
import { SectionCard } from '@/components/greenlight/SectionCard';
import type { HealthSnapshot } from '@greenlight/shared';

/**
 * App containers that exited, as an operator reads them.
 *
 * `crashes` is Kubernetes pod-termination state only. A pipeline step failing a
 * scan is a different event on a different surface, so nothing here says
 * "crash" about one, and the explainer says which of the two this is — the two
 * were routinely confused when the only crash signal was a fingerprint list.
 *
 * Absent or empty means nothing crashed in the window, and the section is left
 * out rather than showing an empty table.
 */
export function CrashesSection({
  snapshot,
}: {
  snapshot: Pick<HealthSnapshot, 'crashes'>;
}): React.ReactElement | null {
  const crashes = snapshot.crashes ?? [];
  if (crashes.length === 0) return null;

  return (
    <SectionCard title="App crashes" className="md:col-span-2">
      <p className="text-xs text-muted-foreground mb-4">
        App containers that exited in the last reporting window. Pipeline step failures are separate
        — they appear on the app's own pipeline view.
      </p>
      <ul className="space-y-2">
        {crashes.map((crash) => (
          <li
            key={crash.fingerprint}
            className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1 border-b border-border pb-2 last:border-0 last:pb-0"
          >
            <span className="text-sm font-medium text-foreground">{appLabel(crash)}</span>
            <span className="text-sm text-muted-foreground">
              {humanCrashReason(crash.reason, crash.exit_code)}
            </span>
            <MonoValue>{formatWhen(crash.occurred_at)}</MonoValue>
          </li>
        ))}
      </ul>
    </SectionCard>
  );
}

/**
 * The app a crash belongs to. The slug is what a reader recognises; an id is the
 * fallback, and a control-plane container has neither.
 */
export function appLabel(crash: { app_slug?: string | null; app_id: string | null }): string {
  return crash.app_slug ?? crash.app_id ?? 'unknown app';
}

/**
 * Kubelet reasons are terse and a couple are jargon. Only the ones a reader
 * misreads are translated; anything unfamiliar is shown as reported rather than
 * guessed at, and the exit code rides along when the platform gave one.
 */
export function humanCrashReason(reason: string, exitCode: number | null): string {
  const known: Record<string, string> = {
    OOMKilled: 'ran out of memory',
    Error: 'exited with an error',
    CrashLoopBackOff: 'restarting repeatedly',
    Evicted: 'evicted by the node',
    ContainerStatusUnknown: 'status unknown',
  };
  const label = known[reason] ?? reason;
  return exitCode === null ? label : `${label} (exit ${exitCode})`;
}

function formatWhen(iso: string): string {
  const at = new Date(iso);
  return Number.isNaN(at.getTime()) ? iso : at.toISOString().slice(0, 16).replace('T', ' ');
}
